import {
  BatchContactOperation,
  BatchProgramAttendeeOperation,
  ContactCreateParams,
  MappedField,
  ProgramAttendeeCreateParams,
} from '@citruscamps/citrus-client'
import { useHandleAttendeeImport } from '../../../../Attendees/pages/AttendeeImport/hooks/useHandleAttendeeImport'
import { useHandleContactImport } from '../../../../Contacts/pages/ContactImport/hooks/useHandleContactImport'

export type ImportItemType = 'contacts' | 'attendees' | 'registrations' | 'events' | 'notes'

export interface IParseFileImportOptions {
  itemTypes: ImportItemType[]
  file: File
}

export interface IBulkImportCreateOptions extends Record<ImportItemType, any[]> {
  itemTypes: ImportItemType[]
  contacts: ContactCreateParams[]
  attendees: ProgramAttendeeCreateParams[]
}

export type AmountOfObjects = 'one' | 'many'

export interface BatchImportOperation {
  total_operations: number
  finished_operations: number
  errored_operations: number
  contacts?: BatchContactOperation
  attendees?: BatchProgramAttendeeOperation
}

interface IProps {
  programId?: string
}

interface ImportData {
  handleParseFile: (options: IParseFileImportOptions) => Promise<MappedField[]>
  handleBulkCreate: (values: IBulkImportCreateOptions) => Promise<BatchImportOperation>
}

export const useHandleImport = ({ programId }: IProps): ImportData => {
  const { handleParseFile: handleParseAttendeeFile, handleBulkCreate: handleAttendeeBulkCreate } =
    useHandleAttendeeImport({
      programId,
    })
  const { handleParseFile: handleParseContactFile, handleBulkCreate: handleContactBulkCreate } =
    useHandleContactImport({
      programId,
    })

  const handleParseFile = async ({
    itemTypes,
    file,
  }: IParseFileImportOptions): Promise<MappedField[]> => {
    const fields: MappedField[] = []
    if (itemTypes.includes('contacts')) {
      const contactFields = await handleParseContactFile(file)
      fields.push(...contactFields)
    }
    if (itemTypes.includes('attendees')) {
      const attendeeFields = await handleParseAttendeeFile(file)
      fields.push(...attendeeFields)
    }
    return fields
  }

  const handleBulkCreate = async (
    options: IBulkImportCreateOptions,
  ): Promise<BatchImportOperation> => {
    const { itemTypes } = options
    let contacts: BatchContactOperation | undefined
    if (itemTypes.includes('contacts')) {
      contacts = await handleContactBulkCreate(options.contacts)
    }
    let attendees: BatchProgramAttendeeOperation | undefined
    if (itemTypes.includes('attendees')) {
      attendees = await handleAttendeeBulkCreate(options.attendees)
    }
    const totalOperations: number =
      (contacts?.total_operations || 0) + (attendees?.total_operations || 0)
    const finishedOperations: number =
      (contacts?.finished_operations || 0) + (attendees?.finished_operations || 0)
    const erroredOperations: number =
      (contacts?.errored_operations || 0) + (attendees?.errored_operations || 0)
    return {
      total_operations: totalOperations,
      finished_operations: finishedOperations,
      errored_operations: erroredOperations,
      contacts,
      attendees,
    }
  }

  return {
    handleParseFile,
    handleBulkCreate,
  }
}
